import { Fragment, useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest, openApiFile } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  ApprovalLevels,
  approverNames,
  type ApprovalTrailStep,
} from '../../components/ApprovalLevels.js';
import { LastWorkPanel } from '../../components/LastWorkAtLocation.js';
import { useAutoRefresh } from '../../../../shared/hooks/useAutoRefresh.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import {
  workflowSubjectFieldText,
  type WorkflowSubject,
} from '../../../../shared/contracts/workflow-subject.js';

/** Nothing further will happen to a request in one of these. */
const SETTLED = new Set(['APPROVED', 'REJECTED', 'CANCELLED', 'RECALLED']);

type TrailStep = ApprovalTrailStep;

/** T049 workflow instance as GET /v1/wf/instances/:instanceId returns it. */
interface WorkflowInstance {
  readonly instanceId: string;
  readonly instanceRefNumber: string;
  readonly entityType: string;
  readonly entityRefId: string;
  readonly entityRefNumber: string;
  readonly instanceStatus: string;
  readonly initiatorUserId: string;
  readonly initiatorName: string | null;
  readonly initiatorLoginId: string | null;
  readonly submittedAt: string;
  readonly subject: WorkflowSubject | null;
  readonly stepTrail: readonly TrailStep[];
  readonly currentStepSequence: number | null;
  readonly canAct: boolean;
  /** Where this kind of request is decided (owner, 19 Oct: Branding Review for branding requests). */
  readonly decisionUrl: string | null;
}

export function ApprovalDetailPage(): ReactNode {
  const { t } = useTranslation();
  const { instanceId } = useParams<{ instanceId: string }>();
  const { token } = useAuth();
  const { money, decimal } = useBusinessNumber();
  const { day, at } = useBusinessDay();
  const [data, setData] = useState<WorkflowInstance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<string | null>(null);
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<WorkflowInstance> =>
    apiRequest<WorkflowInstance>(`/v1/wf/instances/${instanceId}`, { token }),
  [instanceId, token]);

  useEffect(() => {
    let alive = true;
    load()
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [load]);

  // A level cleared by someone else must show here without a refresh. A failed
  // read leaves the screen as it is: nobody asked for it, so nobody is told.
  useAutoRefresh(
    async () => {
      if (busy) return;
      try { setData(await load()); } catch { /* keep what is on screen */ }
    },
    { active: data !== null && !SETTLED.has(data.instanceStatus) },
  );

  async function decide(action: 'approve' | 'reject') {
    // POST /reject requires remarks; the UI refuses rather than sending an empty one.
    if (action === 'reject' && !remarks.trim()) { setError(t('approvals.detail.remarksRequired')); return; }
    setBusy(true); setError(null);
    try {
      await apiRequest(`/v1/wf/instances/${instanceId}/${action}`, {
        method: 'POST', token,
        body: action === 'reject' ? { remarks: remarks.trim() } : { remarks: remarks.trim() || undefined },
      });
      setDecision(action);
      // Re-read so the tracker shows the level just cleared and who holds the next one.
      setData(await load());
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }

  if (error && !data) return <p role="alert">{error}</p>;
  if (!data) return <p>{t('approvals.detail.loading')}</p>;
  const requester = data.initiatorName
    ? data.initiatorLoginId
      ? `${data.initiatorName} (${data.initiatorLoginId})`
      : data.initiatorName
    : data.initiatorLoginId ?? data.initiatorUserId;
  const submitted = data.submittedAt ? new Date(data.submittedAt) : null;
  const trail = data.stepTrail ?? [];
  const pending = trail.find((s) => s.stepStatus === 'PENDING');
  return (
    <main aria-label="approval-detail">
      <h1>{data.entityRefNumber || data.instanceRefNumber}</h1>
      <p>{t('approvals.detail.requestedBy', { user: requester })}</p>
      <p aria-label="approval-status">{t('approvals.detail.status', { status: data.instanceStatus })}</p>

      {trail.length > 0 ? (
        <ApprovalLevels steps={trail} />
      ) : null}

      <dl aria-label="payload" style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '0.25rem 1rem', margin: '1rem 0' }}>
        <dt style={DT}>{t('approvals.inbox.colType')}</dt>
        <dd style={DD}>{data.entityType}</dd>
        <dt style={DT}>{t('approvals.inbox.colRef')}</dt>
        <dd style={DD}>{data.entityRefNumber || '—'}</dd>
        <dt style={DT}>{t('approvals.detail.workflowRef')}</dt>
        <dd style={DD}>{data.instanceRefNumber}</dd>
        <dt style={DT}>{t('approvals.inbox.colSubmitted')}</dt>
        <dd style={DD}>
          {submitted && !Number.isNaN(submitted.getTime()) ? at(submitted) : '—'}
        </dd>
      </dl>

      {/* Naming the request is not describing it: an approver deciding a
          vendor needs the PAN, the bank account and the contact, which only
          the raising module can supply. */}
      {data.subject && data.subject.fields.length > 0 ? (
        <section aria-label="request-details" style={{ margin: '1rem 0' }}>
          <h2 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>
            {data.subject.title}
          </h2>
          <dl
            aria-label="request-detail-fields"
            style={{
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              gap: '0.25rem 1rem',
              margin: 0,
            }}
          >
            {data.subject.fields.map((f) => (
              <Fragment key={f.label}>
                <dt style={DT}>{f.label}</dt>
                <dd style={DD}>{workflowSubjectFieldText(f, { money, decimal, day })}</dd>
              </Fragment>
            ))}
          </dl>
        </section>
      ) : null}

      {/* Files the request carries (e.g. a vendor's proofs), opened with the
          approver's own login so the owning module still decides access. */}
      {data.subject?.files && data.subject.files.length > 0 ? (
        <section aria-label="request-files" style={{ margin: '1rem 0' }}>
          <h2 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>{t('approvals.detail.files')}</h2>
          <ul>
            {data.subject.files.map((f) => (
              <li key={f.contentPath}>
                <button type="button" className="tl-btn tl-btn--secondary"
                        onClick={() => openApiFile(f.contentPath, token).catch((e) => setError((e as Error).message))}>
                  {f.label}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* R14: whatever level this is, the approver sees what was last done at
          the job's site — the same list its raiser was shown on creation. */}
      {data.subject?.jobRequestId ? (
        <LastWorkPanel jobRequestId={data.subject.jobRequestId} />
      ) : null}

      {decision ? <p role="status">{t('approvals.detail.decisionRecorded', { decision })}</p> : null}
      {!pending ? (
        decision ? null : <p role="status">{t('approvals.detail.noneOutstanding')}</p>
      ) : data.decisionUrl ? (
        // Decided on its own screen only; this page points there.
        <p role="status" data-testid="decide-elsewhere">
          {t('approvals.detail.decideOn')}{' '}
          <Link to={data.decisionUrl}>{t('approvals.detail.decideOnLink')}</Link>
        </p>
      ) : data.canAct === false ? (
        // Showing buttons to someone the guard will refuse is how a demo breaks.
        <p role="status" data-testid="cannot-act">
          {t('approvals.detail.awaiting', { user: approverNames(pending, t) })}
        </p>
      ) : (
        <div>
          <label>{t('approvals.detail.remarks')}
            <textarea aria-label="remarks" value={remarks} onChange={(e) => { setRemarks(e.target.value); setError(null); }} />
          </label>
          <button type="button" disabled={busy} onClick={() => { void decide('approve'); }}>{t('approvals.detail.approve')}</button>
          <button type="button" disabled={busy} onClick={() => { void decide('reject'); }}>{t('approvals.detail.reject')}</button>
        </div>
      )}
      {error ? <p role="alert">{error}</p> : null}
    </main>
  );
}

const DT: CSSProperties = {
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-text-muted, #5f6368)',
};

const DD: CSSProperties = { margin: 0 };
